import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useLegacyAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';
import type { ReminderAddress } from '../ReminderWizard/ReminderPostalAddresses';

const REMINDERS = 'reminders';

// #region Variables
const LoadDebtorSendingOptionsVariablesSchema = z.object({
  debtorId: z.coerce.number(),
});

export type LoadDebtorSendingOptionsVariables = z.infer<
  typeof LoadDebtorSendingOptionsVariablesSchema
>;
// #endregion Variables

// #region Response
const SendingOptionsResponseSchema = z.object({
  emails: z.array(
    z.object({
      value: z.string(),
      notifications: z.array(z.string()).default([]),
      main_contact: z.boolean().nullish(),
    }),
  ),
  addresses: z.array(
    z.object({
      id: z.coerce.string(),
      attributes: z.object({
        additional_line_1: z.string().nullish(),
        additional_line_2: z.string().nullish(),
        street_name: z.string().nullish(),
        street_number: z.coerce.string().nullish(),
        street_box: z.string().nullish(),
        zip_code: z.string().nullish(),
        city: z.string().nullish(),
        country_code: z.string().nullish(),
        notifications: z.array(z.string()).nullish(),
        main_contact: z.boolean().nullish(),
        use_postal_address: z.boolean().nullish(),
      }),
    }),
  ),
});

export type DebtorSendingDefaults = {
  emails: Array<string>;
  addresses: Array<ReminderAddress>;
};
// #endregion Response

const PLACEHOLDER: DebtorSendingDefaults = { emails: [], addresses: [] };

// Mirrors the fallback chain of getDefaultEmails/getDefaultAddresses in
// shared/utils/contacts: prefer the reminder-tagged contacts, fall back to the
// main contacts, and finally to every contact.
const firstNonEmpty = <T>(...candidates: Array<Array<T>>) =>
  candidates.find(candidate => candidate.length > 0) ?? [];

// MARK: Load function
async function loadDebtorSendingOptionsQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: LoadDebtorSendingOptionsVariables,
): Promise<DebtorSendingDefaults> {
  const instance = await axiosInstance;
  const { data } = await instance.get(`debtors/${variables.debtorId}/sending_options`);

  const parseResult = SendingOptionsResponseSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'debtorSendingOptions');
    throw new SilentError();
  }

  const emails = firstNonEmpty(
    parseResult.data.emails.filter(email => email.notifications.includes(REMINDERS)),
    parseResult.data.emails.filter(email => email.main_contact ?? false),
    parseResult.data.emails,
  ).map(email => email.value);

  const postalAddresses = parseResult.data.addresses.filter(
    address => address.attributes.use_postal_address ?? false,
  );

  const addresses = firstNonEmpty(
    postalAddresses.filter(
      address => address.attributes.notifications?.includes(REMINDERS) ?? false,
    ),
    postalAddresses.filter(address => address.attributes.main_contact ?? false),
    postalAddresses,
  ).map(({ id, attributes }) => ({
    id,
    additional_line_1: attributes.additional_line_1 ?? undefined,
    additional_line_2: attributes.additional_line_2 ?? undefined,
    street_name: attributes.street_name ?? undefined,
    street_number: attributes.street_number ?? undefined,
    street_box: attributes.street_box ?? undefined,
    zip_code: attributes.zip_code ?? undefined,
    city: attributes.city ?? undefined,
    country_code: attributes.country_code ?? undefined,
  }));

  return { emails, addresses };
}

// MARK: Hook
export function useLoadDebtorSendingOptions(
  variables: LoadDebtorSendingOptionsVariables,
  options: QueryOptions<DebtorSendingDefaults> = {},
) {
  const axiosInstance = useLegacyAxiosInstance();

  const queryResult = useQuery<DebtorSendingDefaults, ApiError>({
    queryKey: ['debtorSendingOptions', variables],
    queryFn: () => loadDebtorSendingOptionsQueryFn(axiosInstance, variables),
    placeholderData: previousData => (isDefined(previousData) ? previousData : PLACEHOLDER),
    ...options,
  });

  return addResourceNameToQueryResult<
    DebtorSendingDefaults,
    ApiError,
    'debtorSendingOptions',
    'PreventUndefined'
  >('debtorSendingOptions', queryResult, PLACEHOLDER);
}
